// @ts-nocheck
import { MOCK } from "@/state/app-store";
import { UI } from "@/components/ui";
import { Icon } from "@/components/icons";
import { api } from "@/api/index";

const { $, $$, fmtNum, fmtMoney, fmtPercent, toast, confirmModal, hasPerm, statusBadge, trendArrow, statCard, channelChip, capabilityBadge, pageHeader, emptyState, primaryBtn, ghostBtn, stepIndicator, BUSINESS_COLORS, chartTooltip, chartXAxis, chartYAxis, chartLineSeries, chartBarSeries } = UI;

declare const echarts: any;
declare const Pages: Record<string, any>;
declare const PageHooks: { mounted?: ((path: string) => void) | null };
declare function navigate(path: string): void;
declare function getCurrentPath(): string;
declare function getCurrentPathWithQuery(): string;
declare function renderRoute(): void;

if (typeof window !== "undefined") {
  (window as any).Pages = (window as any).Pages || {};
}

const Pages = (window as any).Pages;

// ============================================================
// 用户与权限管理（管理员功能）
// 角色边界：SDK 管理员=平台级；游戏运营/研发=游戏级授权
// ============================================================

window.Pages = window.Pages || {};

Pages.userList = function () {
  if (!UI.hasPerm("admin")) {
    return UI.emptyState({
      icon: "🔒",
      title: "权限不足",
      desc: "用户管理仅限 SDK 管理员访问",
      action: `<button onclick="navigate('/')" class="px-4 py-2 bg-brand-900 text-white rounded-md text-sm">回到首页</button>`,
    });
  }

  const admins = MOCK.USERS.filter(u => u.role === "sdk_admin");
  const operators = MOCK.USERS.filter(u => u.role === "operator");
  const developers = MOCK.USERS.filter(u => u.role === "developer");
  const gameLevelUsers = MOCK.USERS.filter(u => u.role !== "sdk_admin");

  return `
    ${UI.pageHeader({
      title: "用户与角色",
      desc: "管理平台级 SDK 管理员、游戏级运营/研发角色，以及按游戏授权范围",
      actions: `
        <button onclick="UI.toast('打开添加 SDK 管理员表单', 'info')" class="px-3 py-1.5 text-sm border border-ink-200 rounded-md hover:bg-ink-50 text-ink-700">+ 添加 SDK 管理员</button>
        <button onclick="UI.toast('打开邀请游戏成员表单', 'info')" class="px-3 py-1.5 text-sm bg-brand-900 text-white rounded-md hover:bg-brand-800">+ 邀请游戏成员</button>
      `,
    })}

    <!-- 角色定义卡片 -->
    <div class="grid grid-cols-3 gap-4 mb-4">
      ${roleDefinitionCard({
        title: "SDK 管理员",
        scope: "平台级 / 全局",
        count: admins.length,
        tone: "danger",
        desc: "EasyShare 产品技术人员，不隶属于某一款游戏，拥有所有接入游戏的 SDK/平台管理权限。",
        permissions: ["管理员功能", "全局统计", "SDK 版本", "渠道管理", "所有游戏可见"],
      })}
      ${roleDefinitionCard({
        title: "游戏运营",
        scope: "游戏级 / 按游戏授权",
        count: operators.length,
        tone: "brand",
        desc: "面向游戏运营人员，按游戏授权，拥有运营管理下的分享落地页配置、模版库、分享卡片配置、分享活动配置、包体运营、分享管理和数据分析权限。",
        permissions: ["分享落地页配置", "模版库", "分享卡片配置", "分享活动配置", "包体运营", "分享管理", "数据分析"],
      })}
      ${roleDefinitionCard({
        title: "游戏研发",
        scope: "游戏级 / 按游戏授权",
        count: developers.length,
        tone: "ok",
        desc: "面向游戏研发人员，按游戏授权，拥有游戏管理、包体管理、SDK 监控和 SDK 文档权限。",
        permissions: ["游戏管理", "包体管理", "SDK 监控", "SDK 文档"],
      })}
    </div>

    <!-- 权限边界说明 -->
    <div class="card p-4 mb-4 border-l-4 border-l-brand-700">
      <div class="flex items-start gap-3">
        <div class="w-8 h-8 rounded-md bg-brand-50 border border-brand-100 flex items-center justify-center flex-shrink-0">${Icon.info({ size: 16, color: "#1E3A8A" })}</div>
        <div class="text-sm text-ink-700 leading-relaxed">
          <div class="font-semibold text-ink-900 mb-1">权限模型说明</div>
          <div>SDK 管理员是 EasyShare 平台级角色，不需要绑定到某款游戏；游戏运营和游戏研发是游戏级角色，必须按游戏授予访问范围。</div>
        </div>
      </div>
    </div>

    <!-- 平台级 SDK 管理员 -->
    <div class="card mb-4 overflow-hidden">
      <div class="px-5 py-4 border-b border-ink-100 flex items-center justify-between">
        <div>
          <h3 class="text-sm font-semibold text-ink-900">平台级 SDK 管理员</h3>
          <p class="text-xs text-ink-500 mt-0.5">EasyShare 产品/技术团队成员，对所有游戏拥有平台管理权限</p>
        </div>
        <span class="badge bg-danger-50 text-danger-700 border border-danger-100">全局权限 · 不按游戏授权</span>
      </div>
      <table class="w-full text-sm">
        <thead class="bg-ink-50 text-xs text-ink-500">
          <tr>
            <th class="text-left px-4 py-2.5 font-medium">用户</th>
            <th class="text-left px-4 py-2.5 font-medium">邮箱</th>
            <th class="text-left px-4 py-2.5 font-medium">权限范围</th>
            <th class="text-left px-4 py-2.5 font-medium">最近活跃</th>
            <th class="text-left px-4 py-2.5 font-medium">状态</th>
            <th class="text-right px-4 py-2.5 font-medium">操作</th>
          </tr>
        </thead>
        <tbody>
          ${admins.map(u => `
            <tr class="border-t border-ink-100 row-hover">
              <td class="px-4 py-3">${userCell(u, "danger")}</td>
              <td class="px-4 py-3 text-xs text-ink-500 font-mono">${u.email}</td>
              <td class="px-4 py-3"><span class="badge bg-danger-50 text-danger-700">全部游戏 + 管理员功能</span></td>
              <td class="px-4 py-3 text-xs text-ink-500">${u.lastActive}</td>
              <td class="px-4 py-3">${UI.statusBadge(u.status)}</td>
              <td class="px-4 py-3 text-right">
                <button class="text-xs text-brand-700 hover:underline mr-2">编辑</button>
                <button class="text-xs text-rose-600 hover:underline">停用</button>
              </td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    </div>

    <!-- 游戏级授权 -->
    <div class="card mb-4 overflow-hidden">
      <div class="px-5 py-4 border-b border-ink-100 flex items-center justify-between">
        <div>
          <h3 class="text-sm font-semibold text-ink-900">游戏级成员授权</h3>
          <p class="text-xs text-ink-500 mt-0.5">管理游戏运营与游戏研发在不同游戏下的访问范围</p>
        </div>
        <div class="flex items-center gap-2">
          <select class="text-xs border border-ink-200 rounded-md px-2.5 py-1.5 bg-white">
            <option>全部游戏</option>
            ${MOCK.GAMES.map(g => `<option>${g.name}</option>`).join("")}
          </select>
          <select class="text-xs border border-ink-200 rounded-md px-2.5 py-1.5 bg-white">
            <option>全部角色</option>
            <option>游戏运营</option>
            <option>游戏研发</option>
          </select>
          <input type="text" placeholder="搜索用户名 / 邮箱" class="w-48 text-xs border border-ink-200 rounded-md px-3 py-1.5" />
        </div>
      </div>
      <table class="w-full text-sm">
        <thead class="bg-ink-50 text-xs text-ink-500">
          <tr>
            <th class="text-left px-4 py-2.5 font-medium">用户</th>
            <th class="text-left px-4 py-2.5 font-medium">邮箱</th>
            <th class="text-left px-4 py-2.5 font-medium">游戏级角色</th>
            <th class="text-left px-4 py-2.5 font-medium">已授权游戏</th>
            <th class="text-left px-4 py-2.5 font-medium">权限范围</th>
            <th class="text-left px-4 py-2.5 font-medium">最近活跃</th>
            <th class="text-left px-4 py-2.5 font-medium">状态</th>
            <th class="text-right px-4 py-2.5 font-medium">操作</th>
          </tr>
        </thead>
        <tbody>
          ${gameLevelUsers.map(u => {
            const isOperator = u.role === "operator";
            const games = u.games.map(id => MOCK.GAMES.find(g => g.id === id)?.name).filter(Boolean);
            return `
              <tr class="border-t border-ink-100 row-hover">
                <td class="px-4 py-3">${userCell(u, isOperator ? "brand" : "ok")}</td>
                <td class="px-4 py-3 text-xs text-ink-500 font-mono">${u.email}</td>
                <td class="px-4 py-3">${roleBadge(u.role)}</td>
                <td class="px-4 py-3"><div class="flex flex-wrap gap-1">${games.map(n => `<span class="badge bg-ink-100 text-ink-700">${n}</span>`).join(" ")}</div></td>
                <td class="px-4 py-3"><span class="text-xs px-2 py-0.5 rounded ${isOperator ? "bg-brand-50 text-brand-700" : "bg-ok-50 text-ok-700"}">${isOperator ? "运营管理" : "研发工具"}</span></td>
                <td class="px-4 py-3 text-xs text-ink-500">${u.lastActive}</td>
                <td class="px-4 py-3">${UI.statusBadge(u.status)}</td>
                <td class="px-4 py-3 text-right">
                  <button onclick="UI.toast('打开游戏授权范围编辑', 'info')" class="text-xs text-brand-700 hover:underline mr-2">授权游戏</button>
                  <button onclick="UI.toast('打开角色变更弹窗', 'info')" class="text-xs text-brand-700 hover:underline mr-2">变更角色</button>
                  <button class="text-xs text-rose-600 hover:underline">停用</button>
                </td>
              </tr>
            `;
          }).join("")}
        </tbody>
      </table>
    </div>

    <!-- 游戏授权矩阵 -->
    <div class="card mb-4 overflow-hidden">
      <div class="px-5 py-4 border-b border-ink-100">
        <h3 class="text-sm font-semibold text-ink-900">游戏授权矩阵</h3>
        <p class="text-xs text-ink-500 mt-0.5">按游戏查看已授权的运营与研发人数；SDK 管理员不纳入单游戏矩阵</p>
      </div>
      <table class="w-full text-sm">
        <thead class="bg-ink-50 text-xs text-ink-500">
          <tr>
            <th class="text-left px-4 py-2.5 font-medium">游戏</th>
            <th class="text-center px-4 py-2.5 font-medium">游戏运营</th>
            <th class="text-center px-4 py-2.5 font-medium">游戏研发</th>
            <th class="text-left px-4 py-2.5 font-medium">授权成员</th>
            <th class="text-right px-4 py-2.5 font-medium">操作</th>
          </tr>
        </thead>
        <tbody>
          ${MOCK.GAMES.map(g => {
            const ops = operators.filter(u => u.games.includes(g.id));
            const devs = developers.filter(u => u.games.includes(g.id));
            const names = [...ops, ...devs].map(u => u.name);
            return `
              <tr class="border-t border-ink-100 row-hover">
                <td class="px-4 py-3"><span class="mr-1.5">${g.icon}</span><span class="font-medium text-ink-900">${g.name}</span></td>
                <td class="px-4 py-3 text-center"><span class="num font-semibold text-brand-700">${ops.length}</span></td>
                <td class="px-4 py-3 text-center"><span class="num font-semibold text-ok-700">${devs.length}</span></td>
                <td class="px-4 py-3"><div class="flex flex-wrap gap-1">${names.length ? names.map(n => `<span class="badge bg-ink-100 text-ink-700">${n}</span>`).join(" ") : '<span class="text-xs text-ink-400">暂无授权成员</span>'}</div></td>
                <td class="px-4 py-3 text-right"><button onclick="UI.toast('打开该游戏授权配置', 'info')" class="text-xs text-brand-700 hover:underline">配置授权</button></td>
              </tr>
            `;
          }).join("")}
        </tbody>
      </table>
    </div>

    <!-- 操作日志 -->
    <div class="card overflow-hidden">
      <div class="px-5 py-4 border-b border-ink-100">
        <h3 class="text-sm font-semibold text-ink-900">权限操作日志（保留 180 天）</h3>
        <p class="text-xs text-ink-500 mt-0.5">记录平台管理员新增用户、授权游戏、变更角色、停用账户等关键操作</p>
      </div>
      <table class="w-full text-sm">
        <thead class="bg-ink-50 text-xs text-ink-500">
          <tr>
            <th class="text-left px-4 py-2.5 font-medium">时间</th>
            <th class="text-left px-4 py-2.5 font-medium">用户</th>
            <th class="text-left px-4 py-2.5 font-medium">操作</th>
            <th class="text-left px-4 py-2.5 font-medium">目标</th>
            <th class="text-left px-4 py-2.5 font-medium">详情</th>
          </tr>
        </thead>
        <tbody>
          ${MOCK.AUDIT_LOGS.map(l => `
            <tr class="border-t border-ink-100 row-hover">
              <td class="px-4 py-3 text-xs text-ink-500 font-mono">${l.time}</td>
              <td class="px-4 py-3">${l.user}</td>
              <td class="px-4 py-3">${l.action}</td>
              <td class="px-4 py-3 text-ink-700">${l.target}</td>
              <td class="px-4 py-3 text-xs text-ink-500">${l.detail}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    </div>
  `;
};

function roleDefinitionCard({ title, scope, count, tone, desc, permissions }) {
  const styles = {
    danger: { card: "bg-danger-50 border-danger-100", text: "text-danger-700", line: "border-l-danger-500" },
    brand: { card: "bg-brand-50 border-brand-100", text: "text-brand-700", line: "border-l-brand-600" },
    ok: { card: "bg-ok-50 border-ok-100", text: "text-ok-700", line: "border-l-ok-600" },
  }[tone];
  return `
    <div class="card p-5 border-l-4 ${styles.line}">
      <div class="flex items-start justify-between gap-3">
        <div>
          <div class="text-xs font-semibold ${styles.text}">${scope}</div>
          <div class="text-base font-semibold text-ink-900 mt-1">${title}</div>
        </div>
        <div class="text-right">
          <div class="num text-2xl font-bold ${styles.text}">${count}</div>
          <div class="text-[10px] text-ink-500">人</div>
        </div>
      </div>
      <p class="text-xs text-ink-600 mt-3 leading-relaxed">${desc}</p>
      <div class="mt-3 flex flex-wrap gap-1">
        ${permissions.map(p => `<span class="text-[10px] px-1.5 py-0.5 rounded bg-ink-100 text-ink-700">${p}</span>`).join("")}
      </div>
    </div>
  `;
}

function userCell(u, tone = "brand") {
  const bg = tone === "danger" ? "bg-danger-600" : tone === "ok" ? "bg-ok-600" : "bg-brand-900";
  return `
    <div class="flex items-center gap-2">
      <div class="w-7 h-7 rounded-full ${bg} flex items-center justify-center text-white text-xs font-semibold">${u.name[0]}</div>
      <span class="font-medium text-ink-900">${u.name}</span>
    </div>
  `;
}

function roleBadge(role) {
  if (role === "operator") return `<span class="badge bg-brand-50 text-brand-700 border border-brand-100">游戏运营</span>`;
  if (role === "developer") return `<span class="badge bg-ok-50 text-ok-700 border border-ok-100">游戏研发</span>`;
  return `<span class="badge bg-danger-50 text-danger-700 border border-danger-100">SDK 管理员</span>`;
}

// ---------- 审计日志：管理员高风险操作与权限变更 ----------
Pages.adminAuditLogs = function () {
  if (!UI.hasPerm("admin")) {
    return UI.emptyState({ icon: "lock", title: "权限不足", desc: "审计日志仅 SDK 管理员可访问" });
  }
  const highRiskLogs = [
    { time: "2026-06-16 12:20:11", user: "李管理", type: "凭证", action: "重置 OpenAPI Key", target: "王者征途生产环境", result: "成功" },
    { time: "2026-06-15 19:40:22", user: "李管理", type: "游戏治理", action: "归档游戏", target: "测试 Demo", result: "成功" },
    { time: "2026-06-15 16:02:03", user: "李管理", type: "权限", action: "变更游戏授权", target: "Match Hero / 张三", result: "成功" },
    { time: "2026-06-14 10:25:11", user: "李管理", type: "凭证", action: "重置 Game Secret", target: "TankBlitz", result: "成功" },
  ];
  const logs = [...highRiskLogs, ...(MOCK.AUDIT_LOGS || []).map((l) => ({ time: l.time, user: l.user, type: "权限", action: l.action, target: l.target, result: "成功" }))];
  return `
    ${UI.pageHeader({
      title: "审计日志",
      desc: "集中查看管理员高风险操作、凭证变更、权限授权和配置治理动作，默认保留 180 天。",
      actions: `<button onclick="UI.toast('已导出审计日志筛选结果', 'success')" class="px-3 py-1.5 text-sm border border-ink-200 rounded-md hover:bg-ink-50">导出审计日志</button>`,
    })}

    <div class="grid grid-cols-4 gap-4 mb-5">
      ${UI.statCard({ label: "今日审计事件", value: logs.length, delta: 0, icon: "fileText", color: "brand" })}
      ${UI.statCard({ label: "高风险操作", value: highRiskLogs.length, delta: 0, icon: "alert", color: "amber" })}
      ${UI.statCard({ label: "凭证相关", value: highRiskLogs.filter(l => l.type === "凭证").length, delta: 0, icon: "key", color: "blue" })}
      ${UI.statCard({ label: "保留周期", value: "180天", delta: 0, icon: "clock", color: "green" })}
    </div>

    <div class="card p-4 mb-5">
      <div class="grid grid-cols-12 gap-3 items-end">
        <label class="col-span-3 block"><span class="block text-xs font-medium text-ink-500 mb-1.5">操作类型</span><select class="w-full px-3 py-2.5 text-sm border border-ink-200 rounded-lg bg-white"><option>全部类型</option><option>权限</option><option>凭证</option><option>游戏治理</option><option>配置模型</option></select></label>
        <label class="col-span-3 block"><span class="block text-xs font-medium text-ink-500 mb-1.5">操作者</span><input class="w-full px-3 py-2.5 text-sm border border-ink-200 rounded-lg" placeholder="搜索操作者" /></label>
        <label class="col-span-3 block"><span class="block text-xs font-medium text-ink-500 mb-1.5">目标对象</span><input class="w-full px-3 py-2.5 text-sm border border-ink-200 rounded-lg" placeholder="搜索游戏 / 用户 / Key" /></label>
        <div class="col-span-3 flex justify-end gap-2"><button class="px-4 py-2 text-sm border border-ink-100 rounded-lg hover:bg-ink-50">重置</button><button class="px-4 py-2 text-sm bg-brand-900 text-white rounded-lg hover:bg-brand-800">筛选</button></div>
      </div>
    </div>

    <div class="card overflow-hidden">
      <table class="w-full text-sm">
        <thead class="bg-ink-50 text-xs text-ink-500">
          <tr><th class="text-left px-5 py-2.5 font-medium">时间</th><th class="text-left px-5 py-2.5 font-medium">操作者</th><th class="text-left px-5 py-2.5 font-medium">类型</th><th class="text-left px-5 py-2.5 font-medium">操作</th><th class="text-left px-5 py-2.5 font-medium">目标</th><th class="text-left px-5 py-2.5 font-medium">结果</th></tr>
        </thead>
        <tbody>
          ${logs.map((log) => `
            <tr class="border-t border-ink-100">
              <td class="px-5 py-3 text-xs font-mono text-ink-500">${log.time}</td>
              <td class="px-5 py-3">${log.user}</td>
              <td class="px-5 py-3"><span class="badge ${log.type === "凭证" ? "bg-amber-50 text-amber-700" : log.type === "游戏治理" ? "bg-danger-50 text-danger-700" : "bg-brand-50 text-brand-700"}">${log.type}</span></td>
              <td class="px-5 py-3 text-ink-700">${log.action}</td>
              <td class="px-5 py-3 text-ink-600">${log.target}</td>
              <td class="px-5 py-3"><span class="badge bg-ok-50 text-ok-700">${log.result}</span></td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    </div>
  `;
};

// __window_exports__
// Expose helpers for cross-page / inline onclick compatibility
;(window as any).roleBadge = roleBadge;
;(window as any).roleDefinitionCard = roleDefinitionCard;
;(window as any).userCell = userCell;
